// src/pages/FinancialReports.tsx
import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Download,
  BarChart3,
  LineChart,
  Building,
  Users,
  CreditCard,
  Filter,
  RefreshCw,
} from "lucide-react";
import {
  ResponsiveContainer,
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart as RechartsBarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Area,
  AreaChart,
} from "recharts";
import { financialReportsService } from "../services/financial-reports";
import {
  FinancialReportFilters,
  ComprehensiveFinancialReport,
  FinancialKPI,
} from "../types/financial-reports";
import { getAcademicTerms } from "../services/academicTerms";
import { useQuery } from "@tanstack/react-query";
import { useHorizontalDragScroll } from "../hooks/useHorizontalDragScroll";

const FinancialReports: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState<string | null>(null);
  const [reportData, setReportData] =
    useState<ComprehensiveFinancialReport | null>(null);
  const [kpis, setKpis] = useState<FinancialKPI[]>([]);
  const [activeTab, setActiveTab] = useState<string>("overview");

  // Filter states
  const [filters, setFilters] = useState<FinancialReportFilters>({
    startDate: new Date(new Date().getFullYear(), 0, 1)
      .toISOString()
      .split("T")[0], // Start of current year
    endDate: new Date().toISOString().split("T")[0], // Today
    reportType: "summary",
    groupBy: "month",
  });

  const [showFilters, setShowFilters] = useState(false);

  // Fetch academic terms for filtering
  const { data: academicTermsData } = useQuery({
    queryKey: ["academicTerms"],
    queryFn: getAcademicTerms,
    staleTime: 5 * 60 * 1000,
  });

  const loadFinancialReports = async () => {
    try {
      setLoading(true);
      const response = await financialReportsService.getComprehensiveReport(
        filters
      );
      setReportData(response.data);
    } catch (error: any) {
      let errorMessage = "Failed to load financial reports";

      if (error.response?.status === 403) {
        errorMessage = "You don't have permission to view financial reports";
      } else if (error.response?.status === 404) {
        errorMessage = "Financial reports service not found";
      } else if (error.response?.status >= 500) {
        errorMessage = "Server error occurred while loading financial reports";
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  const loadKPIs = async () => {
    try {
      const response = await financialReportsService.getFinancialKPIs(filters);
      setKpis(response.data);
    } catch (error: any) {
      // Silently handle KPI loading errors as they're supplementary data
    }
  };

  // Load data on component mount and filter changes
  useEffect(() => {
    loadFinancialReports();
    loadKPIs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleFilterChange = (
    key: keyof FinancialReportFilters,
    value: any
  ) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleExportReport = async (format: "pdf" | "excel" | "csv") => {
    try {
      setExportLoading(format);
      toast.loading(`Generating ${format.toUpperCase()} report...`, {
        id: "export",
      });

      const blob = await financialReportsService.exportFinancialReport({
        format,
        includeCharts: true,
        includeDetails: true,
        dateRange: {
          startDate: filters.startDate!,
          endDate: filters.endDate!,
        },
      });

      // Check if blob is valid
      if (!blob || blob.size === 0) {
        throw new Error("Empty file received from server");
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      // Set appropriate file extension
      const extension = format === "excel" ? "xlsx" : format;
      a.download = `financial-report-${
        new Date().toISOString().split("T")[0]
      }.${extension}`;

      try {
        document.body.appendChild(a);
        a.click();
      } finally {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }

      toast.success(`Report exported as ${format.toUpperCase()}`, {
        id: "export",
      });
    } catch (error: any) {
      console.error("Export error:", error);

      let errorMessage = "Failed to export report";

      if (error.response?.status === 403) {
        errorMessage = "You don't have permission to export reports";
      } else if (error.response?.status === 404) {
        errorMessage = "Export service not available";
      } else if (error.response?.status >= 500) {
        errorMessage = "Server error occurred during export";
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage, { id: "export" });
    } finally {
      setExportLoading(null);
    }
  };

  const formatCurrency = (amount: number | null | undefined) => {
    return new Intl.NumberFormat("en-KE", {
      style: "currency",
      currency: "KES",
      currencyDisplay: "code",
    })
      .format(amount || 0)
      .replace("KES", "KSh");
  };

  const formatPercentage = (value: number | null | undefined) => {
    return `${(value || 0).toFixed(1)}%`;
  };

  const getKPIIcon = (name: string) => {
    switch (name.toLowerCase()) {
      case "revenue":
      case "total revenue":
        return <DollarSign className="w-6 h-6 text-white" />;
      case "profit margin":
      case "net profit":
        return <TrendingUp className="w-6 h-6 text-white" />;
      case "collection rate":
        return <CreditCard className="w-6 h-6 text-white" />;
      case "student count":
        return <Users className="w-6 h-6 text-white" />;
      default:
        return <BarChart3 className="w-6 h-6 text-white" />;
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case "up":
        return <TrendingUp className="w-5 h-5 text-emerald-500" />;
      case "down":
        return <TrendingDown className="w-5 h-5 text-red-500" />;
      default:
        return <div className="w-5 h-5 bg-slate-400 rounded-full" />;
    }
  };

  if (loading && !reportData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-96">
            <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-12 text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-slate-200 border-t-blue-500 mx-auto mb-6"></div>
              <span className="text-xl font-bold text-slate-900 block">
                Loading financial reports...
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-3 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="bg-gradient-to-br from-emerald-300 to-green-400 rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-lg">
                <BarChart3 className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">
                  Financial Reports
                </h1>
                <p className="text-sm sm:text-base text-slate-600 font-semibold mt-1">
                  Comprehensive financial analytics and insights
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="bg-white border-2 border-slate-200 rounded-lg sm:rounded-xl px-3 sm:px-6 py-2 sm:py-3 font-bold text-sm sm:text-base text-slate-700 hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2"
              >
                <Filter className="w-4 h-4 sm:w-5 sm:h-5 text-purple-500" />
                <span className="hidden sm:inline">Filters</span>
              </button>
              <div className="relative group">
                <button
                  className={`bg-white border-2 border-slate-200 rounded-lg sm:rounded-xl px-3 sm:px-6 py-2 sm:py-3 font-bold text-sm sm:text-base text-slate-700 hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2 ${
                    exportLoading ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  disabled={!!exportLoading}
                >
                  <Download className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
                  <span className="hidden sm:inline">
                    {exportLoading ? "Exporting..." : "Export"}
                  </span>
                </button>
                <div className="absolute right-0 mt-2 w-48 sm:w-56 bg-white/95 backdrop-blur-xl rounded-xl sm:rounded-2xl border border-slate-200/60 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10 overflow-hidden">
                  <div className="py-2">
                    <button
                      onClick={() => handleExportReport("pdf")}
                      disabled={!!exportLoading}
                      className={`flex items-center px-4 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm font-bold text-slate-700 hover:bg-gradient-to-r hover:from-slate-50 hover:to-blue-50 w-full text-left transition-all duration-200 ${
                        exportLoading === "pdf"
                          ? "bg-gradient-to-r from-slate-50 to-blue-50"
                          : ""
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {exportLoading === "pdf" ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-slate-200 border-t-blue-500 mr-3"></div>
                      ) : null}
                      Export as PDF
                    </button>
                    <button
                      onClick={() => handleExportReport("excel")}
                      disabled={!!exportLoading}
                      className={`flex items-center px-4 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm font-bold text-slate-700 hover:bg-gradient-to-r hover:from-slate-50 hover:to-blue-50 w-full text-left transition-all duration-200 ${
                        exportLoading === "excel"
                          ? "bg-gradient-to-r from-slate-50 to-blue-50"
                          : ""
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {exportLoading === "excel" ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-slate-200 border-t-blue-500 mr-3"></div>
                      ) : null}
                      Export as Excel
                    </button>
                    <button
                      onClick={() => handleExportReport("csv")}
                      disabled={!!exportLoading}
                      className={`flex items-center px-4 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm font-bold text-slate-700 hover:bg-gradient-to-r hover:from-slate-50 hover:to-blue-50 w-full text-left transition-all duration-200 ${
                        exportLoading === "csv"
                          ? "bg-gradient-to-r from-slate-50 to-blue-50"
                          : ""
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {exportLoading === "csv" ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-slate-200 border-t-blue-500 mr-3"></div>
                      ) : null}
                      Export as CSV
                    </button>
                  </div>
                </div>
              </div>
              <button
                onClick={loadFinancialReports}
                disabled={loading}
                className="bg-white border-2 border-slate-200 rounded-lg sm:rounded-xl px-3 sm:px-6 py-2 sm:py-3 font-bold text-sm sm:text-base text-slate-700 hover:shadow-lg hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center gap-2"
              >
                <RefreshCw
                  className={`w-4 h-4 sm:w-5 sm:h-5 text-blue-500 ${
                    loading ? "animate-spin" : ""
                  }`}
                />
                <span className="hidden sm:inline">Refresh</span>
              </button>
            </div>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="bg-white/95 backdrop-blur-xl rounded-xl sm:rounded-2xl border border-slate-200/60 shadow-xl p-4 sm:p-6 mb-6 sm:mb-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 sm:gap-4">
              <div>
                <label className="block text-xs sm:text-sm font-bold text-slate-900 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) =>
                    handleFilterChange("startDate", e.target.value)
                  }
                  className="block w-full px-3 sm:px-4 py-2 sm:py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200/60 rounded-lg sm:rounded-xl text-sm font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-bold text-slate-900 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) =>
                    handleFilterChange("endDate", e.target.value)
                  }
                  className="block w-full px-3 sm:px-4 py-2 sm:py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200/60 rounded-lg sm:rounded-xl text-sm font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-bold text-slate-900 mb-2">
                  Academic Term
                </label>
                <select
                  value={filters.academicTermId || ""}
                  onChange={(e) =>
                    handleFilterChange(
                      "academicTermId",
                      e.target.value || undefined
                    )
                  }
                  className="block w-full px-3 sm:px-4 py-2 sm:py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200/60 rounded-lg sm:rounded-xl text-sm font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                >
                  <option value="">All Terms</option>
                  {academicTermsData?.data?.map((term: any) => (
                    <option key={term._id} value={term._id}>
                      {term.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-bold text-slate-900 mb-2">
                  Report Type
                </label>
                <select
                  value={filters.reportType}
                  onChange={(e) =>
                    handleFilterChange("reportType", e.target.value)
                  }
                  className="block w-full px-3 sm:px-4 py-2 sm:py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200/60 rounded-lg sm:rounded-xl text-sm font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                >
                  <option value="summary">Summary</option>
                  <option value="detailed">Detailed</option>
                  <option value="comparative">Comparative</option>
                </select>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-bold text-slate-900 mb-2">
                  Group By
                </label>
                <select
                  value={filters.groupBy}
                  onChange={(e) =>
                    handleFilterChange("groupBy", e.target.value)
                  }
                  className="block w-full px-3 sm:px-4 py-2 sm:py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200/60 rounded-lg sm:rounded-xl text-sm font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                >
                  <option value="month">Month</option>
                  <option value="quarter">Quarter</option>
                  <option value="year">Year</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* KPI Cards */}
        {kpis.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
            {kpis.slice(0, 4).map((kpi, index) => (
              <div
                key={index}
                className="bg-white/95 backdrop-blur-xl rounded-xl sm:rounded-2xl border border-slate-200/60 shadow-xl p-4 sm:p-6 hover:scale-105 transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="p-2 bg-gradient-to-br from-blue-300 to-indigo-400 rounded-lg sm:rounded-xl shadow-lg">
                      {getKPIIcon(kpi.name)}
                    </div>
                    <div className="ml-3 sm:ml-4">
                      <p className="text-xs sm:text-sm font-bold text-slate-600">
                        {kpi.name}
                      </p>
                      <p className="text-base sm:text-xl font-bold text-slate-900 break-words">
                        {kpi.name.toLowerCase().includes("rate") ||
                        kpi.name.toLowerCase().includes("margin")
                          ? formatPercentage(kpi.value)
                          : kpi.name.toLowerCase().includes("count")
                          ? (kpi.value || 0).toLocaleString()
                          : formatCurrency(kpi.value || 0)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getTrendIcon(kpi.trend)}
                    <span
                      className={`text-sm font-bold ${
                        kpi.trend === "up"
                          ? "text-emerald-600"
                          : kpi.trend === "down"
                          ? "text-red-600"
                          : "text-slate-600"
                      }`}
                    >
                      {kpi.trendPercentage && kpi.trendPercentage > 0
                        ? "+"
                        : ""}
                      {kpi.trendPercentage
                        ? kpi.trendPercentage.toFixed(1)
                        : "0.0"}
                      %
                    </span>
                  </div>
                </div>
                {kpi.target && (
                  <div className="mt-4">
                    <div className="flex justify-between text-sm text-slate-600 font-semibold mb-2">
                      <span>Target</span>
                      <span>
                        {kpi.name.toLowerCase().includes("rate") ||
                        kpi.name.toLowerCase().includes("margin")
                          ? formatPercentage(kpi.target)
                          : formatCurrency(kpi.target)}
                      </span>
                    </div>
                    <div className="w-full bg-gradient-to-r from-slate-200 to-slate-300 rounded-full h-3">
                      <div
                        className="bg-gradient-to-r from-blue-500 to-indigo-600 h-3 rounded-full shadow-md transition-all duration-500"
                        style={{
                          width: `${Math.min(
                            ((kpi.value || 0) / (kpi.target || 1)) * 100,
                            100
                          )}%`,
                        }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Tab Navigation */}
        <div className="bg-white/95 backdrop-blur-xl rounded-xl sm:rounded-2xl border border-slate-200/60 shadow-xl mb-6 sm:mb-8 overflow-hidden">
          <nav className="flex overflow-x-auto lg:overflow-x-visible scrollbar-thin lg:justify-around gap-2">
            {[
              { id: "overview", label: "Overview", icon: BarChart3 },
              { id: "revenue", label: "Revenue", icon: DollarSign },
              { id: "expenses", label: "Expenses", icon: TrendingDown },
              { id: "scholarships", label: "Scholarships", icon: Users },
              { id: "profit-loss", label: "Profit & Loss", icon: TrendingUp },
              { id: "cash-flow", label: "Cash Flow", icon: LineChart },
              { id: "branches", label: "Branches", icon: Building },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-shrink-0 lg:flex-1 flex items-center justify-center gap-1.5 sm:gap-2 py-3 sm:py-4 px-4 sm:px-6 lg:px-3 font-bold text-xs sm:text-sm whitespace-nowrap transition-all duration-300 ${
                  activeTab === tab.id
                    ? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg"
                    : "text-slate-600 hover:bg-gradient-to-r hover:from-slate-50 hover:to-blue-50"
                }`}
              >
                <tab.icon className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.label.split(" ")[0]}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {!reportData && !loading && (
            <div className="bg-white/95 backdrop-blur-xl rounded-xl sm:rounded-2xl border border-slate-200/60 shadow-xl p-6 sm:p-12 lg:p-16">
              <div className="flex flex-col items-center justify-center text-center">
                <div className="bg-gradient-to-br from-amber-300 to-orange-400 rounded-xl sm:rounded-2xl p-6 sm:p-8 shadow-lg mb-4 sm:mb-6">
                  <BarChart3 className="w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 text-white" />
                </div>
                <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-900 mb-2 sm:mb-3">
                  No Financial Data Available
                </h3>
                <p className="text-sm sm:text-base text-slate-600 font-semibold mb-4 sm:mb-6 max-w-md px-4">
                  There's no financial data to display for the selected period.
                  This could be because:
                </p>
                <ul className="text-xs sm:text-sm text-slate-600 font-semibold text-left space-y-2 mb-6 sm:mb-8 bg-gradient-to-r from-slate-50 to-blue-50 rounded-lg sm:rounded-xl p-4 sm:p-6 w-full max-w-md">
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
                    <span>No transactions have been recorded yet</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
                    <span>The selected date range has no data</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
                    <span>The financial reports service is not available</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
                    <span>You may not have permission to view this data</span>
                  </li>
                </ul>
                <button
                  onClick={loadFinancialReports}
                  className="bg-white border-2 border-slate-200 rounded-lg sm:rounded-xl px-6 sm:px-8 py-3 sm:py-4 font-bold text-sm sm:text-base text-slate-700 hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2"
                >
                  <RefreshCw className="w-5 h-5 text-blue-500" />
                  Try Again
                </button>
              </div>
            </div>
          )}

          {activeTab === "overview" && reportData && (
            <FinancialOverview
              reportData={reportData}
              formatCurrency={formatCurrency}
            />
          )}

          {activeTab === "revenue" && reportData && (
            <RevenueAnalysis
              reportData={reportData}
              formatCurrency={formatCurrency}
            />
          )}

          {activeTab === "expenses" && reportData && (
            <ExpenseAnalysis
              reportData={reportData}
              formatCurrency={formatCurrency}
            />
          )}

          {activeTab === "scholarships" && reportData && (
            <ScholarshipsAnalysis
              reportData={reportData}
              formatCurrency={formatCurrency}
            />
          )}

          {activeTab === "profit-loss" && reportData && (
            <ProfitLossAnalysis
              reportData={reportData}
              formatCurrency={formatCurrency}
            />
          )}

          {activeTab === "cash-flow" && reportData && (
            <CashFlowAnalysis
              reportData={reportData}
              formatCurrency={formatCurrency}
            />
          )}

          {activeTab === "branches" && reportData && (
            <BranchComparison
              reportData={reportData}
              formatCurrency={formatCurrency}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Sub-components for each tab
const FinancialOverview: React.FC<{
  reportData: ComprehensiveFinancialReport;
  formatCurrency: (amount: number) => string;
}> = ({ reportData, formatCurrency }) => {
  const tableScrollRef1 = useHorizontalDragScroll<HTMLDivElement>();
  
  if (!reportData || !reportData.summary) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
        <p className="text-gray-500">No financial summary data available</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Summary Cards */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
        <h3 className="text-xl font-bold text-slate-900 mb-6">
          Financial Summary
        </h3>
        <div className="space-y-4">
          <div className="flex justify-between items-center p-3 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl">
            <span className="text-sm font-semibold text-slate-600">
              Total Revenue
            </span>
            <span className="font-bold text-emerald-600 text-lg">
              {formatCurrency(reportData.summary.totalRevenue)}
            </span>
          </div>
          <div className="flex justify-between items-center p-3 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl">
            <span className="text-sm font-semibold text-slate-600">
              Total Expenses
            </span>
            <span className="font-bold text-red-600 text-lg">
              {formatCurrency(reportData.summary.totalExpenses)}
            </span>
          </div>
          <div className="border-t-2 border-slate-200 pt-4 mt-4">
            <div className="flex justify-between items-center p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl mb-2">
              <span className="text-slate-900 font-bold">Net Profit</span>
              <span
                className={`font-bold text-2xl ${
                  reportData.summary.netProfit >= 0
                    ? "text-emerald-600"
                    : "text-red-600"
                }`}
              >
                {formatCurrency(reportData.summary.netProfit)}
              </span>
            </div>
            <div className="flex justify-between items-center px-3 py-2">
              <span className="text-sm font-semibold text-slate-500">
                Profit Margin
              </span>
              <span
                className={`text-sm font-bold ${
                  (reportData.summary.profitMargin || 0) >= 0
                    ? "text-emerald-600"
                    : "text-red-600"
                }`}
              >
                {(reportData.summary.profitMargin || 0).toFixed(1)}%
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Monthly Trends Chart */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
        <h3 className="text-xl font-bold text-slate-900 mb-6">
          Monthly Trends
        </h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <RechartsLineChart data={reportData.monthlyTrends || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey={(item) => {
                  if (item.period.year && item.period.month) {
                    return `${item.period.year}-${item.period.month
                      .toString()
                      .padStart(2, "0")}`;
                  }
                  return JSON.stringify(item.period);
                }}
              />
              <YAxis />
              <Tooltip
                formatter={(value, name) => [
                  formatCurrency(Number(value)),
                  name,
                ]}
                labelFormatter={(label) => `Period: ${label}`}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="revenue"
                stroke="#10b981"
                strokeWidth={2}
                name="Revenue"
              />
              <Line
                type="monotone"
                dataKey="expenses"
                stroke="#ef4444"
                strokeWidth={2}
                name="Expenses"
              />
              <Line
                type="monotone"
                dataKey="netProfit"
                stroke="#3b82f6"
                strokeWidth={2}
                name="Net Profit"
              />
            </RechartsLineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="lg:col-span-2">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Recent Transactions
          </h3>
          <div ref={tableScrollRef1} className="overflow-x-auto scrollbar-thin">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reportData.recentTransactions
                  ?.slice(0, 10)
                  .map((transaction) => (
                    <tr key={transaction._id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(transaction.date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            transaction.type === "income"
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {transaction.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {transaction.description}
                        {transaction.studentName && (
                          <div className="text-xs text-gray-500">
                            {transaction.studentName}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <span
                          className={
                            transaction.type === "income"
                              ? "text-green-600"
                              : "text-red-600"
                          }
                        >
                          {formatCurrency(transaction.amount)}
                        </span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

// Placeholder components for other tabs
const RevenueAnalysis: React.FC<{
  reportData: ComprehensiveFinancialReport;
  formatCurrency: (amount: number) => string;
}> = ({ reportData, formatCurrency }) => {
  const tableScrollRef2 = useHorizontalDragScroll<HTMLDivElement>();
  
  // Prepare data for pie chart
  const revenueData = [
    {
      name: "Collected",
      value: reportData.revenue?.totalPaid || 0,
      fill: "#10b981",
    },
    {
      name: "Pending",
      value: reportData.revenue?.totalPending || 0,
      fill: "#f59e0b",
    },
    {
      name: "Overdue",
      value: reportData.revenue?.totalOverdue || 0,
      fill: "#ef4444",
    },
  ].filter((item) => item.value > 0);

  return (
    <div className="space-y-6">
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
        <h3 className="text-xl font-bold text-slate-900 mb-6">
          Revenue Overview
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-5 rounded-xl border border-blue-100">
            <p className="text-sm text-blue-700 font-bold mb-2">
              Total Collection
            </p>
            <p className="text-2xl font-bold text-blue-900 break-words">
              {formatCurrency(reportData.revenue?.totalPaid || 0)}
            </p>
          </div>
          <div className="bg-gradient-to-br from-amber-50 to-yellow-50 p-5 rounded-xl border border-amber-100">
            <p className="text-sm text-amber-700 font-bold mb-2">Pending</p>
            <p className="text-2xl font-bold text-amber-900 break-words">
              {formatCurrency(reportData.revenue?.totalPending || 0)}
            </p>
          </div>
          <div className="bg-gradient-to-br from-red-50 to-rose-50 p-5 rounded-xl border border-red-100">
            <p className="text-sm text-red-700 font-bold mb-2">Overdue</p>
            <p className="text-2xl font-bold text-red-900 break-words">
              {formatCurrency(reportData.revenue?.totalOverdue || 0)}
            </p>
          </div>
        </div>
      </div>

      {/* Revenue Distribution Pie Chart */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Revenue Distribution
        </h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={revenueData}
                cx="50%"
                cy="50%"
                outerRadius={80}
                dataKey="value"
                label={({ name, percent }) =>
                  `${name} ${(percent * 100).toFixed(0)}%`
                }
              >
                {revenueData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value) => [formatCurrency(Number(value)), "Amount"]}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Payment Methods Analysis */}
      {reportData.paymentMethods && reportData.paymentMethods.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Payment Methods
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <RechartsBarChart data={reportData.paymentMethods}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="method" />
                <YAxis />
                <Tooltip
                  formatter={(value) => [
                    formatCurrency(Number(value)),
                    "Amount",
                  ]}
                />
                <Bar dataKey="amount" fill="#3b82f6" />
              </RechartsBarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

const ExpenseAnalysis: React.FC<{
  reportData: ComprehensiveFinancialReport;
  formatCurrency: (amount: number) => string;
}> = ({ reportData, formatCurrency }) => {
  // Prepare data for expense categories chart
  const expenseColors = [
    "#ef4444",
    "#f59e0b",
    "#10b981",
    "#3b82f6",
    "#8b5cf6",
    "#f97316",
    "#06b6d4",
  ];
  const expenseCategoriesData = (reportData.topExpenseCategories || []).map(
    (category, index) => ({
      ...category,
      fill: expenseColors[index % expenseColors.length],
    })
  );

  return (
    <div className="space-y-6">
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
        <h3 className="text-xl font-bold text-slate-900 mb-6">
          Expense Summary
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-red-50 to-rose-50 p-5 rounded-xl border border-red-100">
            <p className="text-sm text-red-700 font-bold mb-2">
              Total Expenses
            </p>
            <p className="text-2xl font-bold text-red-900 break-words">
              {formatCurrency(reportData.expenses?.totalExpenses || 0)}
            </p>
          </div>
          <div className="bg-gradient-to-br from-emerald-50 to-green-50 p-5 rounded-xl border border-emerald-100">
            <p className="text-sm text-emerald-700 font-bold mb-2">Approved</p>
            <p className="text-2xl font-bold text-emerald-900 break-words">
              {formatCurrency(reportData.expenses?.approvedExpenses || 0)}
            </p>
          </div>
          <div className="bg-gradient-to-br from-amber-50 to-yellow-50 p-5 rounded-xl border border-amber-100">
            <p className="text-sm text-amber-700 font-bold mb-2">Pending</p>
            <p className="text-2xl font-bold text-amber-900 break-words">
              {formatCurrency(reportData.expenses?.pendingExpenses || 0)}
            </p>
          </div>
        </div>
      </div>

      {/* Expense Categories Bar Chart */}
      {expenseCategoriesData.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Top Expense Categories
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <RechartsBarChart data={expenseCategoriesData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="category" />
                <YAxis />
                <Tooltip
                  formatter={(value) => [
                    formatCurrency(Number(value)),
                    "Amount",
                  ]}
                />
                <Bar dataKey="amount" fill="#ef4444" />
              </RechartsBarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Expense Categories Pie Chart */}
      {expenseCategoriesData.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Expense Distribution
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={expenseCategoriesData}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="amount"
                  label={({ category, percentage }) =>
                    `${category} ${percentage?.toFixed(0) || 0}%`
                  }
                >
                  {expenseCategoriesData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => [
                    formatCurrency(Number(value)),
                    "Amount",
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

const ProfitLossAnalysis: React.FC<{
  reportData: ComprehensiveFinancialReport;
  formatCurrency: (amount: number) => string;
}> = ({ reportData, formatCurrency }) => (
  <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
    <h3 className="text-xl font-bold text-slate-900 mb-6">
      Profit & Loss Statement
    </h3>
    <div className="space-y-3">
      <div className="flex justify-between items-center py-3 px-4 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl">
        <span className="font-bold text-slate-700">Revenue</span>
        <span className="text-emerald-600 font-bold text-lg break-words">
          {formatCurrency(reportData.summary.totalRevenue)}
        </span>
      </div>
      <div className="flex justify-between items-center py-3 px-4 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl">
        <span className="font-bold text-slate-700">Operating Expenses</span>
        <span className="text-red-600 font-bold text-lg break-words">
          -{formatCurrency(reportData.summary.totalExpenses)}
        </span>
      </div>
      <div className="flex justify-between items-center py-4 px-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200 mt-4">
        <span className="font-bold text-slate-900 text-lg">
          Net Profit/Loss
        </span>
        <span
          className={`font-bold text-2xl break-words ${
            reportData.summary.netProfit >= 0
              ? "text-emerald-600"
              : "text-red-600"
          }`}
        >
          {formatCurrency(reportData.summary.netProfit)}
        </span>
      </div>
    </div>
  </div>
);

const CashFlowAnalysis: React.FC<{
  reportData: ComprehensiveFinancialReport;
  formatCurrency: (amount: number) => string;
}> = ({ reportData, formatCurrency }) => {
  const tableScrollRef3 = useHorizontalDragScroll<HTMLDivElement>();
  
  // Prepare cash flow data for chart
  const cashFlowData = (reportData.cashFlow || []).slice(0, 30).map((flow) => ({
    date: new Date(flow.date).toLocaleDateString(),
    inflow: flow.cashIn || 0,
    outflow: flow.cashOut || 0,
    netCashFlow: flow.netCashFlow || 0,
  }));

  return (
    <div className="space-y-6">
      {/* Cash Flow Chart */}
      {cashFlowData.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Cash Flow Trend
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={cashFlowData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip
                  formatter={(value) => [
                    formatCurrency(Number(value)),
                    "Amount",
                  ]}
                />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="inflow"
                  stackId="1"
                  stroke="#10b981"
                  fill="#10b981"
                  fillOpacity={0.6}
                  name="Cash In"
                />
                <Area
                  type="monotone"
                  dataKey="outflow"
                  stackId="2"
                  stroke="#ef4444"
                  fill="#ef4444"
                  fillOpacity={0.6}
                  name="Cash Out"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Cash Flow Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Cash Flow Details
        </h3>
        <div ref={tableScrollRef3} className="overflow-x-auto scrollbar-thin">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Period
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Cash In
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Cash Out
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Net Flow
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {reportData.cashFlow?.slice(0, 12).map((flow, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(flow.date).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                    {formatCurrency(flow.cashIn || 0)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
                    {formatCurrency(flow.cashOut || 0)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <span
                      className={
                        (flow.netCashFlow || 0) >= 0
                          ? "text-green-600"
                          : "text-red-600"
                      }
                    >
                      {formatCurrency(flow.netCashFlow || 0)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const ScholarshipsAnalysis: React.FC<{
  reportData: ComprehensiveFinancialReport;
  formatCurrency: (amount: number) => string;
}> = ({ reportData, formatCurrency }) => {
  const scholarships = reportData.scholarships;

  if (!scholarships) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <p className="text-gray-500">No scholarship data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
        <h3 className="text-xl font-bold text-slate-900 mb-6">
          Scholarships Overview
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-5 rounded-xl border border-purple-100">
            <p className="text-sm text-purple-700 font-bold mb-2">
              Total Scholarships
            </p>
            <p className="text-2xl font-bold text-purple-900 break-words">
              {scholarships.totalScholarships || 0}
            </p>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-5 rounded-xl border border-blue-100">
            <p className="text-sm text-blue-700 font-bold mb-2">
              Total Percentage
            </p>
            <p className="text-2xl font-bold text-blue-900 break-words">
              {scholarships.totalPercentage || 0}%
            </p>
          </div>
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-5 rounded-xl border border-emerald-100">
            <p className="text-sm text-emerald-700 font-bold mb-2">
              Average Percentage
            </p>
            <p className="text-2xl font-bold text-emerald-900 break-words">
              {scholarships.averagePercentage?.toFixed(1) || 0}%
            </p>
          </div>
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-5 rounded-xl border border-amber-100">
            <p className="text-sm text-amber-700 font-bold mb-2">
              Total Amount
            </p>
            <p className="text-2xl font-bold text-amber-900 break-words">
              {formatCurrency(scholarships.totalScholarshipAmount || 0)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const BranchComparison: React.FC<{
  reportData: ComprehensiveFinancialReport;
  formatCurrency: (amount: number) => string;
}> = ({ reportData, formatCurrency }) => {
  const tableScrollRef4 = useHorizontalDragScroll<HTMLDivElement>();
  
  // Prepare branch data for chart
  const branchData = (reportData.branchReports || []).map((branch) => ({
    branchName: branch.branchName,
    revenue: branch.revenue,
    expenses: branch.expenses,
    profit: branch.profit,
    students: branch.studentCount,
  }));

  return (
    <div className="space-y-6">
      {/* Branch Performance Chart */}
      {branchData.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Branch Revenue & Expenses
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <RechartsBarChart data={branchData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="branchName" />
                <YAxis />
                <Tooltip
                  formatter={(value) => [
                    formatCurrency(Number(value)),
                    "Amount",
                  ]}
                />
                <Legend />
                <Bar dataKey="revenue" fill="#10b981" name="Revenue" />
                <Bar dataKey="expenses" fill="#ef4444" name="Expenses" />
                <Bar dataKey="profit" fill="#3b82f6" name="Net Profit" />
              </RechartsBarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Branch Comparison Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Branch Performance Details
        </h3>
        <div ref={tableScrollRef4} className="overflow-x-auto scrollbar-thin">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Branch
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Revenue
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Expenses
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Profit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Students
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Collection Rate
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {reportData.branchReports?.map((branch) => (
                <tr key={branch.branchId}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {branch.branchName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                    {formatCurrency(branch.revenue)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
                    {formatCurrency(branch.expenses)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <span
                      className={
                        branch.profit >= 0 ? "text-green-600" : "text-red-600"
                      }
                    >
                      {formatCurrency(branch.profit)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {branch.studentCount}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {(branch.collectionRate || 0).toFixed(1)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FinancialReports;
